import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import FullScreenPageHeader from 'components/FullScreenPageHeader';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import PatientAnamnesis from 'components/patient-entry/PatientAnamnesis';
import PatientAppointment from 'components/patient-entry/PatientAppointment';
import {
  PatientCentralConsumerRenderProps,
  withPatientCentralContext
} from 'components/patient-entry/PatientCentralContext';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import {
  PatientEntryConsumerRenderProps,
  PatientEntryContextProviderWrapper,
  withPatientEntryContext
} from 'components/patient-entry/PatientEntryContext';
import PatientEntrySummary from 'components/patient-entry/PatientEntrySummary';
import PatientSearch from 'components/patient-entry/PatientSearch';
import UpsertPatient, { PatientEditMode } from 'components/patient-entry/upsert/UpsertPatient';
import moment from 'moment';
import React, { Component, Fragment } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps, RouterProps } from 'react-router';
import { Button, Tab } from 'semantic-ui-react';
import {
  addInterventionBooking,
  editInterventionBooking,
  getInterventionBooking,
  getSecondDoseBookingData
} from 'services/interventionBookingServices';
import { findPatientById } from 'services/patientServices';
import styled from 'styled-components';
import { InterventionExternalCode } from 'ts-types/api.enums';
import {
  BookingDataDto, DoctorDto,
  PatientDto,
  PatientResponse,
  SecondDoseDto,
  TreatmentBookingDto,
  UpsertPatientDto
} from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';


const MainContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .page-header .title {
    font-size: 1.1rem !important;
    color: #444444;
  }

  .pane-content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const StyledPraxSegment = styled(PraxStyledSegment)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 !important;
  margin: 0 !important;
  height: 100%;
`;

const NewPatientDiv = styled.div`
  display: block;
  margin: 10px 5px;
`;

const PatientEntryDiv = styled.div`
  display: block;
  margin: 10px 5px;
  overflow: hidden;
`;

const NewPatientActionsDiv = styled.div`
  margin: 10px 10px;
`;

const StyledTab = styled(Tab)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .ui.segment > div {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ui.tab.active, .ui.tab.open {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    margin: 0;
    width: inherit;
  }

  .ui.secondary.pointing.menu .item {
    cursor: default;
  }

  .ui.secondary.pointing.menu a.item:active {
    border-color: transparent;
  }

  .ui.menu .item .tab-menu-indicator {
    display: inline-block;
    margin-left: 0.5rem;
    font-size: 0.85rem;
    color: #5a5a5a;
    line-height: 1rem;
    letter-spacing: 0.07rem;
  }

  .ui.attached.tabular.menu {
    padding-left: 0.85rem;
    border: none !important;
  }

  .ui.attached.segment {
    border: none !important;
  }

  .ui.segment {
    border: none !important;
  }
`;

type UrlParams = { id: string };

export enum UpsertMode {
  ADD,
  EDIT
}

interface Props extends PatientEntryConsumerRenderProps,
    PatientCentralConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps,
    MandateConsumerRenderProps,
    AuthConsumerRenderProps,
    RouteComponentProps<UrlParams>,
    RouterProps,
    MessageSourceProps {
}

interface State {
  mode: UpsertMode,
  activeTabIndex: number,
  patientKey: number,
  patientId: number,
  patient?: UpsertPatientDto,
  treatmentBooking?: Partial<TreatmentBookingDto>,
  bookingData?: Partial<BookingDataDto>,
  bookingPerformed: boolean,
  showBookingSummary: boolean,
  bookingSubmit: boolean,
  secondDoseData?: SecondDoseDto,
  referralDoctor?: DoctorDto,
  cancelTokenSource: CancelTokenSource
}

class PatientEntryViewComponent extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {getAllMandates} = this.props;

    getAllMandates();

    const bookingId = this.props.match.params.id;

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      mode: bookingId ? UpsertMode.EDIT : UpsertMode.ADD,
      activeTabIndex: 0,
      patientKey: 1,
      patientId: -1,
      bookingPerformed: false,
      showBookingSummary: false,
      bookingSubmit: false,
      treatmentBooking: {
        date: moment().format('YYYY-MM-DD'),
        time: moment().format('HH:mm'),
        remarks: ""
      },
      cancelTokenSource
    };

    const parentBookingId = this.props.parentBookingId;
    if (parentBookingId) {
      this.fetchParentBooking(parentBookingId)
    } else if (bookingId) {
      this.fetchBooking(parseInt(bookingId));
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchBooking = async (id: number) => {

    const {cancelTokenSource} = this.state;

    const booking = await getInterventionBooking(id, this.state.cancelTokenSource);

    const patientId = booking.patientId;
    const patient = await findPatientById(cancelTokenSource, patientId);

    this.props.setTreatmentBookingCurrent(booking);

    this.setState({
      activeTabIndex: 4,
      patientKey: 1,
      patientId,
      patient,
      treatmentBooking: booking,
      bookingData: booking.bookingData
    });

  };

  fetchParentBooking = async (parentBookingId: number) => {

    const {cancelTokenSource} = this.state;

    const booking = await getInterventionBooking(parentBookingId, this.state.cancelTokenSource);

    const patientId = booking.patientId;
    const patient = await findPatientById(cancelTokenSource, patientId);

    const prevTreatmentBooking = this.state.treatmentBooking;

    this.setState({
      activeTabIndex: 3,
      patientKey: 1,
      patientId,
      patient,
      treatmentBooking: {
        ...prevTreatmentBooking,
        parentBookingId: parentBookingId,
        date: booking.date,
        time: booking.time,
        patientId: booking.patientId,
        tariffId: booking.tariffId,
        reasonId: booking.reasonId,
        interventionId: booking.interventionId,
        questionSetId: booking.questionSetId,
        anamnesisSeverity: booking.anamnesisSeverity,
        questionAnswerPairs: booking.questionAnswerPairs
      },
      bookingData: {}
    });

  };

  goToHome = () => {
    const {history} = this.props;
    history.push("/patient-central");
  };

  setReferralDoctor = (doctor: DoctorDto) => {
    this.setState({
      referralDoctor: doctor
    });
  };

  render(): React.ReactNode {
    const {showBookingSummary, secondDoseData, referralDoctor, activeTabIndex} = this.state;

    const {
      referenceDataFetched,
      patientEntryContextRefDataFetched,
      getMessage
    } = this.props;

    const menuClassnames = (ix: number): string => {
      const cl = ["item"];
      if (activeTabIndex === ix) {
        cl.push("active");
      }
      return cl.join(" ");
    }

    const panes = [
      {
        menuItem: getMessage("patientCentral.patientEntry.title.searchPatient"),
        // pane: {key: "patient", render: this.renderPatientSearch, style: styledPane}
        render: () => this.renderPatientSearch()
      },
      {
        menuItem:
            <Fragment key={`patient1-custom-menu-fragment`}>
              <a className={menuClassnames(1)}>
              <span className="tab-menu-text">
                {getMessage("patientCentral.patientEntry.title.patientEntry")}
              </span>
                <span className="tab-menu-indicator">(1/2)</span>
              </a>
            </Fragment>,
        // pane: {key: "patient1", render: this.renderPatient1Entry, style: styledPane}
        render: () => this.renderPatient1Entry()
      },
      {
        menuItem:
            <Fragment key={`patient2-custom-menu-fragment`}>
              <a className={menuClassnames(2)}>
              <span className="tab-menu-text">
                {getMessage("patientCentral.patientEntry.title.patientEntry")}
              </span>
                <span className="tab-menu-indicator">(2/2)</span>
              </a>
            </Fragment>,
        // pane: {key: "patient2", content: this.renderPatient2Entry(), style: styledPane}
        render: () => this.renderPatient2Entry()
      },
      {
        menuItem: getMessage("patientCentral.patientEntry.title.anamnesis"),
        // pane: {key: "remarks", content: this.renderAnamnesisEntry(), style: styledPane}
        render: () => this.renderAnamnesisEntry()
      },
      {
        menuItem: getMessage("patientCentral.patientEntry.title.appointment"),
        // pane: {key: "gen-codes", content: this.renderAppointmentEntry(), style: styledPane}
        render: () => this.renderAppointmentEntry()
      }
    ];

    return (
        <MainContentDiv>
          <FullScreenPageHeader headerMessage={getMessage("patientCentral.patientEntry.title.main")}
                                buttonMessage={getMessage("back")}
                                onConfirm={this.goToHome}
          />

          <StyledPraxSegment>
            {
              showBookingSummary &&
              <PatientEntrySummary
                  patient={this.state.patient}
                  treatmentBooking={{
                    ...this.state.treatmentBooking,
                    bookingData: this.state.bookingData
                  }}
                  secondDoseData={secondDoseData}
                  referralDoctor={referralDoctor}
              />
            }
            {
              !showBookingSummary && referenceDataFetched && patientEntryContextRefDataFetched &&
              <StyledTab
                  activeIndex={activeTabIndex}
                  menu={{secondary: true, pointing: true}}
                  panes={panes}
                  renderActiveOnly={true}
                  onTabChange={(/*event: React.MouseEvent<HTMLDivElement>, data: TabProps*/) => {
                    // const activeIndex : number = +data.activeIndex!;
                    // this.setState({
                    //   activeTabIndex: activeIndex
                    // });
                  }}
              />
            }
          </StyledPraxSegment>
        </MainContentDiv>
    );
  }

  onPatientSelected = async (patient: PatientResponse) => {
    const {patientKey, cancelTokenSource} = this.state;
    const editedPatient = await findPatientById(cancelTokenSource, patient.id);
    this.setState({
      activeTabIndex: 1,
      patientKey: patientKey + 1,
      patientId: patient.id,
      patient: editedPatient
    });
  }

  onCreateNewPatient = () => {
    const {patientKey} = this.state;
    this.setState({
      activeTabIndex: 1,
      patientKey: patientKey + 1,
      patientId: -1,
      patient: undefined
    });
  }

  onPatient1Save = async (aPatient: PatientDto) => {
    const {patientKey, cancelTokenSource} = this.state;
    const patientId = aPatient.id;
    const editedPatient = await findPatientById(cancelTokenSource, patientId);
    this.setState({
      activeTabIndex: 2,
      patientKey: patientKey + 1,
      patientId: patientId,
      patient: editedPatient
    });
  }

  onCancelPatient1Upsert = () => {
    this.setState({
      activeTabIndex: 0
    });
  }

  onPatient2Save = async (aPatient: PatientDto) => {
    const {patientKey, cancelTokenSource} = this.state;
    const patientId = aPatient.id;
    const editedPatient = await findPatientById(cancelTokenSource, patientId);
    this.setState({
      activeTabIndex: 3,
      patientKey: patientKey + 1,
      patientId: patientId,
      patient: editedPatient
    });
  }

  onCancelPatient2Upsert = () => {
    this.setState({
      activeTabIndex: 1
    });
  }

  onAnamnesisSave = async (treatmentBookingDto?: TreatmentBookingDto) => {
    this.setState({
      activeTabIndex: 4,
      treatmentBooking: treatmentBookingDto
    });
  }

  onCancelAnamnesis = () => {
    this.setState({
      activeTabIndex: 2
    });
  }

  timeout = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  onAppointmentSave = async (
      aPatient?: UpsertPatientDto,
      errorCallBack?: (exception: any) => void) => {

    const {currentUser, getInterventionById} = this.props;
    const {patientId, treatmentBooking, bookingData, cancelTokenSource} = this.state;

    this.setState({
      bookingSubmit: true
    });

    let treatmentBookingData = {
      ...treatmentBooking!
    };

    if (treatmentBooking && treatmentBooking.interventionId) {
      const intervention = getInterventionById(treatmentBooking.interventionId);

      if (intervention && (intervention.externalCode === InterventionExternalCode.VACCINATION_DOSE_1
          || intervention.externalCode === InterventionExternalCode.VACCINATION_DOSE_2)) {

        treatmentBookingData = {
          ...treatmentBooking!,
          tariffId: undefined,
          reasonId: undefined,
          remarks: undefined
        }
      }
    }

    const requestBookingData = {
      ...bookingData
    }

    const treatmentBookingDto: TreatmentBookingDto = {
      patientId: patientId,
      mandateId: currentUser!.userConfig.mandateId,
      ...(treatmentBookingData as TreatmentBookingDto),
      container: treatmentBooking!.container === "NO_CONTAINER" ? undefined : treatmentBooking!.container,
      interventionSlotId: (requestBookingData as any).interventionSlotId,
      secondVaccinationInterventionSlotId: (requestBookingData as any).secondVaccinationInterventionSlotId,
      remarks: (requestBookingData as any).remarks,
      bookingData: requestBookingData as BookingDataDto
    };

    try {
      const bookingIdString = this.props.match.params.id;
      let newInterventionId = undefined;
      if (bookingIdString) {
        const bookingId = parseInt(bookingIdString);
        await editInterventionBooking(bookingId, treatmentBookingDto, cancelTokenSource);
      } else {
        newInterventionId = await addInterventionBooking(treatmentBookingDto, cancelTokenSource);
      }

      this.setState({
        bookingPerformed: true
      });

      if (newInterventionId) {
        const secondDoseData = await getSecondDoseBookingData(newInterventionId, cancelTokenSource);
        this.setState({
          secondDoseData
        });
      } else {
        await this.timeout(1250);
      }

      this.setState({
        showBookingSummary: true
      });
    } catch (e) {
      if (errorCallBack) {
        errorCallBack(e);
      }
    }

    this.setState({
      bookingSubmit: false
    });
  }

  onCancelAppointment = () => {
    this.setState({
      activeTabIndex: 3
    });
  }

  onTreatmentFormUpdate = async (bookingDataDto?: BookingDataDto) => {

    this.setState({
      bookingData: bookingDataDto
    });
  }

  renderPatientSearch() {
    const {getMessage} = this.props;
    return (
        <NewPatientDiv className="pane-content">
          <PatientSearch onPatientSelected={this.onPatientSelected}/>
          <NewPatientActionsDiv>
            <Button compact onClick={() => {
            }}>
              {getMessage("button.cancel")}
            </Button>
            <AddButton
                buttonMessage={getMessage("patientCentral.patientEntry.search.addNewPatient")}
                onClick={this.onCreateNewPatient}
            />
          </NewPatientActionsDiv>
        </NewPatientDiv>
    );
  }

  renderPatient1Entry() {

    const {countries, languages} = this.props;
    const {patientKey, patient, patientId} = this.state;

    return (
        <PatientEntryDiv className="pane-content">
          <UpsertPatient
              editMode={PatientEditMode.BASIC_DATA}
              key={patientKey}
              patient={patient}
              patientId={patientId}
              countries={countries}
              languages={languages}
              onPatientSave={this.onPatient1Save}
              onCancel={this.onCancelPatient1Upsert}
          />
        </PatientEntryDiv>
    );
  }

  renderPatient2Entry() {

    const {countries, languages} = this.props;
    const {patientKey, patient, patientId} = this.state;

    return (
        <PatientEntryDiv className="pane-content">
          <UpsertPatient
              editMode={PatientEditMode.INSURANCE}
              key={patientKey}
              patient={patient}
              patientId={patientId}
              countries={countries}
              languages={languages}
              onPatientSave={this.onPatient2Save}
              onCancel={this.onCancelPatient2Upsert}
          />
        </PatientEntryDiv>
    );
  }

  renderAnamnesisEntry() {

    const {patient, patientId, treatmentBooking, mode} = this.state;

    return (
        <PatientAnamnesis
            className="pane-content"
            patient={patient}
            patientId={patientId}
            treatmentBooking={treatmentBooking}
            onSave={this.onAnamnesisSave}
            onCancel={this.onCancelAnamnesis}
            setReferralDoctor={this.setReferralDoctor}
            mode={mode}
        />
    );
  }

  renderAppointmentEntry() {

    const {patient, bookingPerformed, treatmentBooking, bookingData, bookingSubmit, referralDoctor} = this.state;

    const booking = {
      ...treatmentBooking,
      bookingData: bookingData
    };

    return (
        <PatientAppointment
            className="pane-content"
            patient={patient}
            treatmentBooking={booking}
            onFormUpdate={this.onTreatmentFormUpdate}
            onSave={this.onAppointmentSave}
            onCancel={this.onCancelAppointment}
            bookingPerformed={bookingPerformed}
            bookingSubmit={bookingSubmit}
            setReferralDoctor={this.setReferralDoctor}
            referralDoctor={referralDoctor}
        />
    );
  }

}

const PatientEntryViewComponent1 =
    withMessages(
        withRouterWorkaround(
            withAuthContext(
                withMandateContext(
                    withPatientCentralRefDataContext(
                        withPatientCentralContext(
                            withPatientEntryContext(PatientEntryViewComponent)))))));

export default class PatientEntryView extends Component {
  render() {
    return (
        <PatientEntryContextProviderWrapper>
          <PatientEntryViewComponent1/>
        </PatientEntryContextProviderWrapper>
    );
  }
}

